/**
 * signal-core.ts — Core reactive primitives for the compiled runtime.
 *
 * This module contains the fundamental reactive system that every compiled
 * app needs: signals, effects, memos, batching, and DOM element helpers.
 * Extracted from signals.ts for tree-shakeability.
 *
 * DCE AUDIT (ROADMAP piece 12, 2026-07-10): this module must stay free of
 * module-scope side effects so bundlers can drop it (it is deliberately
 * ABSENT from package.json "sideEffects"). Current module-scope state and
 * why each is DCE-safe:
 *   - `_pendingValueAttrs = []` — pure array literal.
 *   - `_componentStateMap = typeof WeakMap !== 'undefined' ? new WeakMap() : null`
 *     — known-pure constructor; retained only when an export that uses it is.
 *   - the single-instance tripwire (BUG-HARNESS-047) is the ONE top-level
 *     statement, and it is wholly inside `if (typeof __DEV__ !== 'undefined'
 *     && __DEV__)`. Production defines `__DEV__: 'false'`, the bundler folds
 *     the branch away, and what is left is declarations only — the same
 *     `__DEV__`-at-the-call-site rule docs/DEV-DIAGNOSTICS.md states. Verified
 *     by --assert=treeshake and by the size-matrix baseline (0 B delta).
 * Everything else is declarations only (verified: no non-declaration
 * module-scope statements). Empirical guard: analyze-zerok-bundle.mjs
 * --assert=treeshake proves hello/counter drop unused signal-core exports.
 * Keep it this way: no top-level DOM/global touches, no top-level calls that
 * survive a production define — install anything effectful lazily inside the
 * function that needs it.
 */
// The ONLY import this module takes, and deliberately a rule table rather than
// behaviour: `element-create-rules` is declarations plus one pure `new Set`, so
// the DCE audit above still holds (BUG-RUNTIME-141).
import { INERT_CREATE_TAGS, createHostElement } from '../../shared/element-create-rules.js';
const __IS_EXPORT__ = typeof __EXPORT__ !== 'undefined' && __EXPORT__;
// ---------------------------------------------------------------------------
// Global tracking state
// ---------------------------------------------------------------------------
export let currentListener = null;
export let batchDepth = 0;
export let pendingEffects = [];
// Global effect execution depth — prevents cross-effect infinite loops.
// When effect A triggers effect B which triggers effect A etc., this counter
// grows. If it exceeds MAX_EFFECT_DEPTH, we stop executing effects to break
// the cycle. This is analogous to React's "Maximum update depth exceeded" error.
export let effectDepth = 0;
export const MAX_EFFECT_DEPTH = 100;
// Sentinel returned by renderCompiledComponent when memo custom compare
// says props are equal — tells replaceComponentNode to keep existing DOM.
export const MEMO_SKIP = /*#__PURE__*/ Symbol('MEMO_SKIP');
// ---------------------------------------------------------------------------
// Pending value attribute updates for controlled inputs
// ---------------------------------------------------------------------------
// React only updates the value HTML attribute (setAttribute) with a one-render
// delay — it updates the DOM property (el.value) immediately but defers the
// attribute to the next render cycle. We match this by queuing setAttribute
// calls and flushing them at the start of the next effect execution.
// EPIC-MOON-002 R5: the controlled-input value-attr queue lives in
// compiled/value-attrs.ts and registers its flush here on first use.
// Graphs with no `value=` binding never import it, and the flush call
// sites below collapse to a null check.
let _valueAttrFlush = null;
export function _setValueAttrFlush(fn) {
    _valueAttrFlush = fn;
}
export function flushPendingValueAttrs() {
    if (_valueAttrFlush)
        _valueAttrFlush();
}
// ---------------------------------------------------------------------------
// Component state persistence
// ---------------------------------------------------------------------------
// Effect capture: when set to an array, deferEffect runs effects synchronously
// and captures disposers. Used by renderCompiledComponent to track child effects
// for disposal when the component is unmounted.
export let _effectCapture = null;
// Component state persistence — mirrors React's hooks state slots.
// When a compiled component is re-rendered (via createEffect in parent),
// its signals need to persist across re-creations (like React's useState).
// _componentStateMap: WeakMap<key, { signals: Array<[getter, setter]>, disposers: Array<Function>, deferCount: number }>
// _componentStateKey: current component's persistence key (set by caller)
// _componentSlotIndex: current hook slot index during component execution
export const _componentStateMap = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
export let _componentStateKey = null;
export let _componentSlotIndex = 0;
// Track effect disposers created during a component render
export let _componentEffectDisposers = null;
// Track deferred effect slot index during component render (for mount-effect dedup)
export let _componentDeferIndex = 0;
if (typeof __DEV__ !== 'undefined' && __DEV__) {
    const _g = globalThis;
    const _here = new Error('signal-core instantiated here').stack;
    if (_g.__JSC_SIGNAL_CORE__) {
        const _msg = 'JetShake: signal-core was instantiated TWICE in one process (BUG-HARNESS-047).\n' +
            '  This module holds _componentStateKey, _componentSlotIndex, _componentDeferIndex,\n' +
            '  _componentEffectDisposers, _anchorScopeKey and _componentStateMap as module-scope\n' +
            '  state. Two copies do not share any of it, so the compiled-to-compat seam loses the\n' +
            '  state key and every hosted compat child fresh-mounts on every parent re-render.\n' +
            '  first instantiation:\n' + _g.__JSC_SIGNAL_CORE__ + '\n' +
            '  this instantiation:\n' + _here + '\n' +
            '  Usual cause: one road reaches the runtime through the ESM build/src graph and\n' +
            '  another through testing/third-party/cjs-bundles/core.cjs, which inlines its own\n' +
            '  copy. Under the CT harness, testing/compile-through/single-runtime.mjs bridges\n' +
            '  the CJS road onto the ESM instance and must be the FIRST setup file.';
        // Armed by a harness that has declared it needs one instance: hard error.
        // Everywhere else in DEV: warn once, so a new split substrate announces
        // itself without taking an unrelated suite down with it.
        if (_g.__JSC_SINGLE_RUNTIME__)
            throw new Error(_msg);
        if (!_g.__JSC_SIGNAL_CORE_WARNED__) {
            _g.__JSC_SIGNAL_CORE_WARNED__ = true;
            console.warn(_msg);
        }
    }
    else {
        _g.__JSC_SIGNAL_CORE__ = _here;
    }
}
// ---------------------------------------------------------------------------
// Branch disposal scope (BUG-RUNTIME-088)
// ---------------------------------------------------------------------------
// A compiled conditional swap tears its old branch down by REMOVING that
// branch's DOM. That is enough for anything the branch owns inside its own
// subtree, and nothing at all for what it owns OUTSIDE it: a `createPortal`
// call in the branch parks the portalled nodes in a foreign container, and
// their only teardown channel is the disposer of the effect that opened them.
// Nothing collected that disposer, so the portal survived its owner and a
// re-mount appended a SECOND copy - one per open/close cycle.
//
// `_branchScope` gives the swap site an explicit owner: while it is set, every
// `createEffect` created during the branch build also registers its disposer
// here, and the swap disposes the previous run's array before rebuilding. It is
// opt-in per call site precisely because "dispose every nested effect when the
// creating effect re-runs" is NOT generally true - a keyed list re-runs its
// effect while REUSING rows, and blanket ownership would tear those rows'
// bindings down under them. A conditional swap is different by construction:
// it removes every node of the previous branch unconditionally, so everything
// that branch built is already garbage by the time this runs.
export let _branchDisposers = null;
/** Enter a branch disposal scope; returns the previous one to restore. */
export function _branchScope(arr) {
    const prev = _branchDisposers;
    _branchDisposers = arr;
    return prev;
}
// ---------------------------------------------------------------------------
// Owner disposal scope (BUG-RUNTIME-187)
// ---------------------------------------------------------------------------
// A keyed row builds a whole subtree - its own bindings AND every effect any
// nested compiled component creates while rendering into it. Those effects
// subscribe to signals that OUTLIVE the row (the parent's state), and a
// subscriber set is a strong reference, so a removed row that never disposed
// its effects is retained by the very signal it was reading: measured on the
// krausest reducer app, every 1,000-row create/clear cycle left 12,000 detached
// nodes and ~1.3 MB alive, and every later write to that signal re-ran the dead
// bindings against detached DOM.
//
// `_componentEffectDisposers` cannot carry this: it is per component state key,
// so a nested component's effects land in ITS entry, not the row's, and the row
// teardown never sees them. `_branchDisposers` is the right shape but the wrong
// lifetime - it is drained when the SWAP re-runs, and a keyed list re-runs its
// effect while reusing rows. This scope is owned by the row: installed for one
// row's render, drained only when that row is removed or replaced.
export let _ownerDisposers = null;
/** Enter an owner disposal scope; returns the previous one to restore. */
export function _ownerScope(arr) {
    const prev = _ownerDisposers;
    _ownerDisposers = arr;
    return prev;
}
/**
 * Record that `listener` now subscribes to `subs`, so disposing it can take it
 * back out again. Without this a disposed effect stays in every set it ever
 * read from: inert (execute() returns early) but immortal, and holding its
 * closure - which is the DOM it built - for the lifetime of the signal.
 *
 * One array per effect, appended only on a NEW subscription, so a re-running
 * effect that re-reads the same signals adds nothing.
 */
export function _track(subs, listener) {
    const before = subs.size;
    subs.add(listener);
    if (subs.size === before)
        return;
    const deps = listener._deps;
    if (deps)
        deps.push(subs);
    else
        listener._deps = [subs];
}
/** Remove `listener` from every subscriber set it joined. */
export function _untrackDeps(listener) {
    const deps = listener._deps;
    if (!deps)
        return;
    for (let i = 0; i < deps.length; i++)
        deps[i].delete(listener);
    listener._deps = null;
}
/**
 * Register a teardown function on whatever disposal scopes are currently open,
 * exactly as `createEffect` registers its own `dispose` (see the three pushes
 * at the bottom of createEffect). For a subscription that is NOT an effect -
 * `compiledUseSyncExternalStore`'s `subscribe()` return value - this is the
 * only channel that can ever unsubscribe it.
 *
 * The returned wrapper is idempotent: a disposer is reachable from more than
 * one channel (a component re-render AND the enclosing branch swap both drain
 * their arrays), and an unsubscribe must not run twice (TASK-CT-013 D4).
 */
export function registerDisposer(fn) {
    let done = false;
    const dispose = () => {
        if (done)
            return;
        done = true;
        try {
            fn();
        }
        catch (_e) { /* a store's unsubscribe must not break teardown */ }
    };
    if (_componentEffectDisposers)
        _componentEffectDisposers.push(dispose);
    if (_branchDisposers)
        _branchDisposers.push(dispose);
    if (_effectCapture)
        _effectCapture.push(dispose);
    return dispose;
}
// ---------------------------------------------------------------------------
// Setters for mutable module-scoped state
// ---------------------------------------------------------------------------
// Other modules need to mutate these variables. Since ES module exports are
// live bindings but can only be mutated from the declaring module, we expose
// setter functions.
export function setCurrentListener(v) { currentListener = v; }
export function setBatchDepth(v) { batchDepth = v; }
export function setPendingEffects(v) { pendingEffects = v; }
export function setEffectDepth(v) { effectDepth = v; }
export function set_effectCapture(v) { _effectCapture = v; }
export function set_componentStateKey(v) { _componentStateKey = v; }
export function set_componentSlotIndex(v) { _componentSlotIndex = v; }
export function set_componentEffectDisposers(v) { _componentEffectDisposers = v; }
export function set_componentDeferIndex(v) { _componentDeferIndex = v; }
// ---------------------------------------------------------------------------
// Anchor allocation scope (BUG-RUNTIME-056)
// ---------------------------------------------------------------------------
// `_anchorSlot` (render-component.ts) hands each anchor CALL SITE a persistent
// slot, resolved POSITIONALLY from the component's state entry against a cursor
// (`entry._anchorIndex`) that `setComponentStateKey` resets once per compat
// render (BUG-RUNTIME-061). That discipline is sound only while a body is
// invoked from a render pass.
//
// A body RE-INVOKED FROM INSIDE A DEFERRED EFFECT breaks both halves of it, and
// both were measured:
//   - `_componentStateKey` is null during a deferred effect run (it is installed
//     only around a body invocation), so `_anchorSlot` misses the slot map
//     entirely and mints brand-new scaffolding on every run;
//   - even with the key reinstated the cursor keeps ADVANCING, so each run takes
//     the NEXT slot and never the one it took last time.
// Either alone makes an anchor allocated under an effect non-persistent - and a
// non-persistent anchor hands its child's ref a brand-new DOM node per run,
// which for embla (whose `ref` IS a `useState` setter) is an unconditional state
// change per render.
//
// So a re-invoking effect captures this scope and reinstates it for every run.
// The CAPTURE POINT is load-bearing: the anchors belong to the body being
// re-invoked and are allocated BY it, so the scope must be taken BEFORE the
// first invocation. A scope captured afterwards restores a cursor that has
// already walked past them, and every re-run allocates one slot too far.
export let _anchorScopeKey = null;
/**
 * Copy of a per-field anchor cursor, or null. Both halves of the scope need a
 * SNAPSHOT rather than the live object - capture must survive the body then
 * advancing it, and enter must not let a re-run's advance write back into the
 * stored scope - so they are the same operation twice.
 */
function _copyCursor(o) {
    return o ? Object.assign({}, o) : null;
}
/** Snapshot the anchor cursor as it stands now. Null when there is nothing to persist against. */
export function captureAnchorScope() {
    const key = _componentStateKey;
    if (!key || !_componentStateMap)
        return null;
    const entry = _componentStateMap.get(key);
    if (!entry)
        return null;
    return { key, base: _copyCursor(entry._anchorIndex) };
}
/** Reinstate a captured scope for one re-invocation. Returns a token for `exitAnchorScope`. */
export function enterAnchorScope(scope) {
    if (!scope || !_componentStateMap)
        return null;
    const entry = _componentStateMap.get(scope.key);
    if (!entry)
        return null;
    const saved = { entry, index: entry._anchorIndex, key: _anchorScopeKey };
    entry._anchorIndex = _copyCursor(scope.base);
    _anchorScopeKey = scope.key;
    return saved;
}
export function exitAnchorScope(saved) {
    if (!saved)
        return;
    saved.entry._anchorIndex = saved.index;
    _anchorScopeKey = saved.key;
}
// ---------------------------------------------------------------------------
// DOM helpers — short names for common DOM operations.
// The compiler emits these instead of document.createElement etc.
// to reduce output size (~20 bytes saved per element after minification).
// ---------------------------------------------------------------------------
export function _el(tag) {
    // BUG-RUNTIME-141: React builds `<script>` off the ordinary route so the
    // node is parser-inserted and can never execute. One `Set.has` on the
    // element-creation hot path buys the framework's security posture back;
    // see src/shared/element-create-rules.ts for why nothing cheaper works.
    if (INERT_CREATE_TAGS.has(tag))
        return createHostElement(document, tag);
    return document.createElement(tag);
}
export function _svg(tag) { return document.createElementNS('http://www.w3.org/2000/svg', tag); }
export function _tn(text) { return document.createTextNode(text); }
export function _cm(text) { return document.createComment(text || ''); }
export function _frag() { return document.createDocumentFragment(); }
/** Append a static text node to parent. Replaces parent.appendChild(document.createTextNode('...')) */
export function _t(parent, text) { parent.appendChild(document.createTextNode(text)); }
/**
 * className binding with React semantics: nullish/boolean removes the class
 * attribute entirely (React omits it; `el.className = undefined` would
 * stringify to class="undefined"). Works on SVG too, where className is a
 * readonly SVGAnimatedString and only setAttribute works.
 */
export function setClassName(el, v) {
    if (v == null || typeof v === 'boolean')
        el.removeAttribute('class');
    else if (typeof el.className === 'string')
        el.className = v;
    else
        el.setAttribute('class', v);
}
/**
 * Shallow-equality helper used by compiler-emitted context provider effects.
 *
 * When an author writes `<Ctx.Provider value={{ foo, bar, baz }}>...</Ctx.Provider>`
 * with reactive `foo`/`bar`/`baz`, the compiler wraps the provider write in a
 * createEffect. Each run allocates a fresh object, which by identity alone looks
 * "new" to the context signal — and because subscribers are notified on every
 * non-Object.is write, a single dependency change can cascade into a loop when
 * downstream consumers re-trigger the effect via their own updates (for example
 * through `useSyncExternalStore` notifications).
 *
 * Guarding the write with a shallow comparison lets the common case (same keys,
 * same primitive/stable values as last render) short-circuit without touching
 * the signal. Deeper changes still propagate — callers only call this for the
 * object-literal path, so arrays/non-plain objects fall back to referential
 * equality via Object.is inside the signal itself.
 *
 * Function values are treated as equal to each other (BUG-RUNTIME-008): the
 * extremely common pattern `<Ctx.Provider value={{ open, setOpen, doThing }}>`
 * allocates fresh function identities on every parent re-render. React users
 * are advised to `useCallback` these; in JetShake we treat such same-shape
 * callback closures as identity-equal so that a location change (or any other
 * parent re-render) doesn't force every consumer to re-render. The compiled
 * closures read latest signal values anyway, so behaviour is preserved. The
 * trade-off: if a user legitimately swaps one callback for a different one
 * (e.g. `onClick: condition ? A : B`) the consumer will use the last written
 * function until some other key in the value object changes — a rare pattern
 * usually paired with a companion state flag, which itself would invalidate
 * equality.
 */
export function _shallowEqualObj(a, b) {
    if (Object.is(a, b))
        return true;
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object')
        return false;
    // Only compare plain objects — if either side has a prototype or is an array,
    // defer to identity (let the signal's Object.is decide).
    if (Array.isArray(a) || Array.isArray(b))
        return false;
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length)
        return false;
    for (let i = 0; i < ak.length; i++) {
        const k = ak[i];
        if (!Object.prototype.hasOwnProperty.call(b, k))
            return false;
        const av = a[k];
        const bv = b[k];
        if (Object.is(av, bv))
            continue;
        // Treat two functions as equal: see BUG-RUNTIME-008. This matches the
        // Svelte/Solid mental model where callback closures are not a semantic
        // "change" — the body is typically stable across renders.
        if (typeof av === 'function' && typeof bv === 'function')
            continue;
        return false;
    }
    return true;
}
// ---------------------------------------------------------------------------
// Diagnostic hook — optional signal creation interceptor.
// When diagnostics are active, this function (if set) receives every
// [getter, setter] pair just before it's returned from createSignal and
// may return a wrapped pair to proxy reads/writes.
//
// The cost in production (diagnostics off) is a single null check per signal
// creation. No runtime cost on reads or writes.
// ---------------------------------------------------------------------------
export let _signalCreationHook = null;
export function setSignalCreationHook(fn) {
    _signalCreationHook = fn;
}
// ---------------------------------------------------------------------------
// createSignal — replaces useState
// ---------------------------------------------------------------------------
/**
 * Returns [getter, setter] where getter is a function that returns
 * the current value and registers the caller as a dependent.
 *
 * Optimized for minified output:
 * - Shared `resolve` helper deduplicates the typeof-function check
 *   across write() and _writeSilent (saves ~15 bytes minified per signal factory).
 * - writeSilent is an inline closure on read instead of a named function declaration.
 * - Subscriber notification uses inline spread to avoid a temporary variable.
 */
export function createSignal(initialValue) {
    // Component state persistence: if we're inside a renderCompiledComponent call
    // with a state key, check for persisted signal from a previous render.
    // This mirrors React's useState which returns persisted state on re-renders.
    if (!__IS_EXPORT__ && _componentStateKey && _componentStateMap) {
        const entry = _componentStateMap.get(_componentStateKey);
        if (entry && entry.signals && _componentSlotIndex < entry.signals.length) {
            return entry.signals[_componentSlotIndex++];
        }
    }
    // Support lazy initializer like useState(() => expensiveComputation())
    let value = typeof initialValue === 'function' ? initialValue() : initialValue;
    const subscribers = new Set();
    function read() {
        if (currentListener)
            _track(subscribers, currentListener);
        return value;
    }
    read._isSignalGetter = true;
    // Shared resolver: deduplicates the functional-update typeof check
    // that would otherwise appear in both write() and _writeSilent.
    function resolve(v) { return typeof v === 'function' ? v(value) : v; }
    // Silent write: update value without notifying subscribers.
    // Used for context save/restore in nested providers.
    read._writeSilent = function (v) { value = resolve(v); };
    function write(v) {
        v = resolve(v);
        if (Object.is(value, v))
            return;
        value = v;
        if (batchDepth > 0) {
            // In a batch — defer effect notifications but immediately mark
            // memo computations as dirty. This ensures memos return fresh values
            // when read during the batch flush, even if the memo's computation
            // hasn't been explicitly executed yet.
            for (const sub of subscribers) {
                if (sub._isMemoComputation)
                    sub.execute();
                else if (!pendingEffects.includes(sub))
                    pendingEffects.push(sub);
            }
        }
        else {
            // Snapshot subscribers before notifying (subscriber callbacks may modify the set)
            for (const sub of [...subscribers])
                sub.execute();
        }
    }
    let pair = [read, write];
    // Diagnostic hook — may return a wrapped [getter, setter] pair.
    // Persisted pair is the wrapped one so re-renders reuse the same identity.
    if (_signalCreationHook) {
        const wrapped = _signalCreationHook(pair, value);
        if (wrapped)
            pair = wrapped;
    }
    // Persist the signal pair for component state persistence
    if (!__IS_EXPORT__ && _componentStateKey && _componentStateMap) {
        let entry = _componentStateMap.get(_componentStateKey);
        if (!entry) {
            entry = { signals: [], disposers: [], deferCount: 0 };
            _componentStateMap.set(_componentStateKey, entry);
        }
        entry.signals.push(pair);
        _componentSlotIndex++;
    }
    return pair;
}
// ---------------------------------------------------------------------------
// createMemo — replaces useMemo
// ---------------------------------------------------------------------------
/**
 * Creates a derived computation that automatically tracks its
 * dependencies and only recomputes when they change.
 */
export function createMemo(fn) {
    let value;
    let dirty = true;
    const subscribers = new Set();
    const computation = {
        _isMemoComputation: true,
        execute() {
            dirty = true;
            // Notify our own subscribers (propagate dirty flags through memo chain)
            const toNotify = [...subscribers];
            for (const sub of toNotify) {
                if (sub._isMemoComputation) {
                    sub.execute(); // Eagerly propagate to downstream memos
                }
                else if (batchDepth > 0) {
                    if (!pendingEffects.includes(sub))
                        pendingEffects.push(sub);
                }
                else {
                    sub.execute();
                }
            }
        }
    };
    function read() {
        if (currentListener) {
            _track(subscribers, currentListener);
        }
        if (dirty) {
            const prevListener = currentListener;
            currentListener = computation;
            try {
                value = fn();
            }
            finally {
                currentListener = prevListener;
            }
            dirty = false;
        }
        return value;
    }
    // Same brand as createSignal getters — custom-hook consumers use
    // brand-safe reads (BUG-COMPILER-011/012) that only invoke branded getters.
    read._isSignalGetter = true;
    return read;
}
// ---------------------------------------------------------------------------
// _g — brand a compiler-emitted getter thunk
// ---------------------------------------------------------------------------
/**
 * Brands an inline getter thunk the compiler emits (reactive prop values,
 * custom-hook return getters) with the same _isSignalGetter mark that
 * createSignal/createMemo reads carry. Brand-guarded unwrap sites
 * (_resolveFunctionProps, the custom-hook resolving memos) only invoke
 * branded functions, so a plain function VALUE — a useCallback result, an
 * event handler, react-modal's parentSelector — is never called as a getter
 * (BUG-COMPILER-011/012).
 */
export function _g(fn) {
    fn._isSignalGetter = true;
    return fn;
}
// ---------------------------------------------------------------------------
// _pv - observe a PROP as its value (BUG-COMPILER-042)
// ---------------------------------------------------------------------------
/**
 * BUG-COMPILER-042 - the receiver side of `_g` for a FORWARDED prop.
 *
 * `_g` brands the thunk a compiled parent puts in a child's props object so
 * `<Child step={props.step}/>` stays lazy. Every consumer that is not compiled
 * source gets that thunk resolved for it - `_resolveFunctionProps` at the
 * compat seam, `appendCompiled` at a text child, `applyProps` on a host
 * element. A COMPILED child reading `props.step` in its own source had no such
 * seam: the compiler emitted the whole-param unwrap (`typeof props ===
 * 'function' ? props() : props`), which resolves a props OBJECT handed over as
 * a thunk but never the per-property getter sitting inside it. So the child
 * observed the function: `typeof` read "function", `===` read false,
 * `JSON.stringify` dropped the key, and string coercion wrote the accessor's
 * source text into the DOM (ATLAS-ECOSYSTEM-009).
 *
 * So the compiler emits `__pv(...)` around a param property read, and the
 * resolve lives here next to the brand it undoes.
 *
 * TWO properties this must keep, and they pull in opposite directions:
 *
 * 1. BRAND-GUARDED. Only OUR getters are invoked. A plain function VALUE - a
 *    useCallback result, an event handler, react-modal's parentSelector - is
 *    returned by identity. Invoking those is BUG-COMPILER-011/012, which cost
 *    a prop each time.
 *
 * 2. TRACKED, deliberately - the opposite choice from `_hv`, which untracks.
 *    The call site is the child's own reactive scope (the attribute effect,
 *    the derived memo), so invoking the getter there is precisely what
 *    subscribes THAT scope to the parent's signal - the whole point of
 *    delivering the prop lazily. `_hv` untracks because it resolves at a
 *    BAILED consumer, where the enclosing scope is a compiled ancestor's and
 *    subscribing it is the BUG-RUNTIME-072 anchor livelock. Untracking here
 *    would instead freeze every forwarded prop at its first value.
 *
 * The laziness itself is not this function's to keep: nothing is read until
 * the child's scope runs. Resolving EAGERLY at the forward site is what
 * BUG-RUNTIME-072 forbids, and that site is untouched.
 */
export function _pv(v) {
    return (typeof v === 'function' && v._isSignalGetter === true) ? v() : v;
}
// ---------------------------------------------------------------------------
// _hv - un-brand a custom hook's result for a consumer we did NOT lower
// ---------------------------------------------------------------------------
/**
 * BUG-RUNTIME-084 - the other half of the `_g` contract.
 *
 * A compiled custom hook hands its state back as a branded getter so the read
 * stays lazy and lands in the CONSUMER's own memo (BUG-RUNTIME-072 sub-family
 * A). Every JetShake-compiled consumer gets that unwrap emitted for it:
 *
 *   const __hookRaw_0 = useProgress();
 *   const __hook_1 = createMemo(() =>
 *     typeof __hookRaw_0 === 'function' && __hookRaw_0._isSignalGetter
 *       ? __hookRaw_0() : __hookRaw_0);
 *
 * A consumer the compiler BAILED on has no memo to put the read in and no
 * unwrap emitted, so `const { valueText } = useProgress()` destructures a
 * FUNCTION and every field is `undefined` - silent, no throw. This is the
 * consumer-side residual risk 072-A named when it shipped `__g`.
 *
 * So the compiler emits `__hv(useProgress())` around custom-hook calls inside
 * bailed bodies, and the resolve lives here, next to the brand it undoes:
 *
 *   - a branded getter resolves to its value (the measured case: a compiled
 *     hook whose return is a `readContext` pass-through)
 *   - a branded getter in an ARRAY slot resolves in place (`const [v, setV] =
 *     useToggle()`), copying only when there is something to resolve so a
 *     plain `useState` tuple keeps its identity
 *   - everything else is returned untouched, by identity. React's own hooks
 *     and third-party hooks return unbranded values through compat, so the
 *     wrapper is inert for them.
 *
 * Object returns are deliberately NOT walked: `return { x }` is lowered to an
 * object of `get` accessors, so a compat consumer destructuring it already
 * reads values. Walking it would mean touching foreign properties (and could
 * fire third-party accessors) to fix a shape that is not broken.
 *
 * The read is UNTRACKED. A bailed component can be rendered inside a compiled
 * reactive scope; subscribing that scope to the hook's state is the exact
 * shape of the BUG-RUNTIME-072 anchor livelock. Compat consumers re-render
 * through compat's own path, so they do not need the subscription.
 */
export function _hv(v) {
    if (typeof v === 'function') {
        return v._isSignalGetter === true ? untrack(v) : v;
    }
    if (Array.isArray(v)) {
        let out = null;
        for (let i = 0; i < v.length; i++) {
            const el = v[i];
            if (typeof el === 'function' && el._isSignalGetter === true) {
                if (out === null)
                    out = v.slice();
                out[i] = untrack(el);
            }
        }
        return out === null ? v : out;
    }
    return v;
}
// ---------------------------------------------------------------------------
// _spr - a REACTIVE SPREAD in a custom hook's returned object literal
// ---------------------------------------------------------------------------
/**
 * BUG-COMPILER-040 - the last untouched sub-shape of the return literal.
 *
 * `return { ...result, mutate }` where `result` is a branded getter spreads a
 * FUNCTION. A function has no own enumerable properties, so the consumer got
 * `{}` (or `{_isSignalGetter:true}`) and every key the spread was meant to
 * supply read `undefined`. Not stale - empty, and silent: nothing throws.
 * tanstack-query's `useMutation` is that line verbatim.
 *
 * BUG-COMPILER-052 lifted the COMPUTING SLOTS of a return literal into `get`
 * accessors over hoisted memos and deliberately left spreads alone, because a
 * spread has no single slot to lift into. This is the helper that gives it one.
 *
 * The compiler splits the literal into ordered parts and hands them here:
 *
 *   return { a, ...result, b }
 *     ->  const _sprSrc0 = createMemo(() => result());
 *         return __spr({ a }, _sprSrc0, { b });
 *
 * A FUNCTION part is a lazy spread source; anything else is a literal chunk.
 * The compiler only ever emits object literals for the chunks, so the typeof
 * test is unambiguous. Parts are applied in source order, so later parts
 * overwrite earlier ones - which is exactly JS spread precedence, in both
 * directions (`{...src, mutate}` keeps the literal's `mutate`,
 * `{mutate, ...src}` keeps the source's).
 *
 * Three properties this must keep:
 *
 * 1. THE READS STAY LAZY, in the CONSUMER's scope. Each spread key becomes a
 *    live `get` accessor that calls the source, so the read subscribes whoever
 *    touches the property - the consumer's own memo, or its compat render -
 *    not the hook's setup scope. That is the BUG-RUNTIME-072 constraint, and
 *    it is why the tempting `{...src(), mutate}` is wrong: a compiled hook
 *    body runs once, so an eager spread is frozen at mount for a compiled
 *    caller (refuted by measurement, not by argument - see the ticket).
 *
 * 2. THE RESULT IS A PLAIN OBJECT, for every caller kind. Accessors are the
 *    object contract already (`_hv` does not walk objects precisely because
 *    `return { x }` is lowered to accessors), so a compiled consumer, a compat
 *    consumer and an uncompiled consumer invoked by the compiled runtime all
 *    read values without knowing the protocol. A whole-object branded thunk
 *    would be correct only for the first of the three.
 *
 * 3. THE KEY SCAN IS UNTRACKED. Enumerating the source runs here, in the hook
 *    body, i.e. inside whatever reactive scope called the hook. Subscribing
 *    that scope is the 072 anchor livelock, so the scan untracks; only the
 *    per-key accessors track, and they run in the consumer.
 *
 * KNOWN LIMIT - the key SET is snapshotted, the VALUES are live. A key the
 * source grows after the hook body ran has no accessor. That is the honest
 * cost of not using a Proxy (which would have to intercept `ownKeys` on an
 * object identity the library never sees, for a shape that is stable in every
 * measured case: an observer result object carries all its keys from the
 * first snapshot). On the compat path the hook body re-runs per render, so the
 * set is re-taken there anyway.
 *
 * Assignment is supported: writing a spread key replaces the accessor with a
 * plain data property, so the write wins over the live read instead of
 * throwing in strict mode.
 */
export function _spr(...parts) {
    const out = {};
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (typeof part !== 'function') {
            // A literal chunk. Descriptors, not Object.assign: the chunk may hold
            // `get` accessors from the 052 slot lift, and copying them by value
            // would resolve them here - eagerly, in the hook's scope.
            if (part != null)
                Object.defineProperties(out, Object.getOwnPropertyDescriptors(part));
            continue;
        }
        const src = part;
        const snapshot = untrack(src);
        // `{...null}` / `{...undefined}` is a no-op; a primitive spreads whatever
        // own enumerable keys its wrapper has (a string's indices, nothing else).
        if (snapshot == null)
            continue;
        const descs = Object.getOwnPropertyDescriptors(Object(snapshot));
        for (const key of Reflect.ownKeys(descs)) {
            if (!descs[key].enumerable)
                continue;
            Object.defineProperty(out, key, {
                enumerable: true,
                configurable: true,
                get() {
                    const v = src();
                    return v == null ? undefined : v[key];
                },
                set(v) {
                    Object.defineProperty(out, key, {
                        value: v, writable: true, enumerable: true, configurable: true,
                    });
                },
            });
        }
    }
    return out;
}
// ---------------------------------------------------------------------------
// createEffect — replaces useEffect
// ---------------------------------------------------------------------------
/**
 * Creates a side effect that automatically tracks its dependencies.
 * Re-runs when any dependency changes. Returns a cleanup function.
 */
/**
 * Run and clear an effect's portal cleanups.
 *
 * `compiledCreatePortal` parks the removal of the nodes it appended into a
 * foreign container on the effect that opened them (`_portalCleanups`), because
 * that container is outside every DOM subtree the effect's own teardown walks.
 * Every teardown of that effect - re-run, batch cleanup phase, and DISPOSE -
 * has to drain it, or the portalled nodes outlive their owner (BUG-RUNTIME-088:
 * dispose was the one that did not).
 */
function _runPortalCleanups(effect) {
    if (effect._portalCleanups) {
        for (const pc of effect._portalCleanups)
            pc();
        effect._portalCleanups = null;
    }
}
export function createEffect(fn, immediate = true) {
    let cleanup = null;
    let disposed = false;
    let running = false;
    let pendingRerun = false;
    let selfRerunCount = 0;
    const MAX_SELF_RERUNS = 50; // Match React's maximum update depth
    const effect = {
        // Phase 1: Run cleanup only (used by batch flush for React-compatible ordering)
        runCleanup() {
            if (disposed)
                return;
            if (cleanup && typeof cleanup === 'function') {
                cleanup();
                cleanup = null;
            }
            _runPortalCleanups(effect);
        },
        // Phase 2: Run setup only (used by batch flush for React-compatible ordering)
        runSetup() {
            if (disposed)
                return;
            if (running) {
                pendingRerun = true;
                return;
            }
            if (effectDepth >= MAX_EFFECT_DEPTH) {
                if (typeof console !== 'undefined') {
                    console.error('Maximum effect execution depth exceeded (' + MAX_EFFECT_DEPTH + ').');
                }
                return;
            }
            flushPendingValueAttrs();
            const prevListener = currentListener;
            currentListener = effect;
            running = true;
            pendingRerun = false;
            effectDepth++;
            try {
                cleanup = fn();
            }
            catch (err) {
                if (err && typeof err.then === 'function') {
                    effectDepth--;
                    running = false;
                    currentListener = prevListener;
                    err.then(() => { if (!disposed)
                        effect.execute(); });
                    return;
                }
                throw err;
            }
            finally {
                effectDepth--;
                running = false;
                currentListener = prevListener;
            }
            if (pendingRerun) {
                pendingRerun = false;
                selfRerunCount++;
                if (selfRerunCount <= MAX_SELF_RERUNS) {
                    setTimeout(() => effect.execute(), 0);
                }
                else {
                    selfRerunCount = 0;
                }
            }
            else {
                selfRerunCount = 0;
            }
        },
        execute() {
            if (disposed)
                return;
            // Guard against self-triggering: if this effect is currently running,
            // don't re-enter — just mark it for re-run after completion.
            // This matches React's semantics where setState inside useEffect
            // doesn't synchronously re-run the same effect.
            if (running) {
                pendingRerun = true;
                return;
            }
            // Guard against cross-effect infinite loops (A triggers B triggers A...).
            // If we've exceeded the max depth, bail out with a warning.
            if (effectDepth >= MAX_EFFECT_DEPTH) {
                if (typeof console !== 'undefined') {
                    console.error('Maximum effect execution depth exceeded (' + MAX_EFFECT_DEPTH + '). ' +
                        'This likely indicates a circular dependency between effects.');
                }
                return;
            }
            // Run cleanup from previous execution
            if (cleanup && typeof cleanup === 'function') {
                cleanup();
            }
            // Clean up any portal content from previous execution
            _runPortalCleanups(effect);
            // Flush any queued value attribute updates before running the effect body.
            // DOM-binding effects queue setAttribute via queueValueAttr, but the actual
            // setAttribute call is deferred to flushPendingValueAttrs. If a useEffect
            // reads DOM attributes (e.g. getAttribute('value')), it needs them to be
            // current. Without this flush, effects see stale attribute values because
            // they run in the same batch as the DOM-binding effects that queued the update.
            flushPendingValueAttrs();
            // Track dependencies
            const prevListener = currentListener;
            currentListener = effect;
            running = true;
            pendingRerun = false;
            effectDepth++;
            try {
                cleanup = fn();
            }
            catch (err) {
                // Thrown Promise (lazy/Suspense) — schedule re-run when promise resolves
                if (err && typeof err.then === 'function') {
                    effectDepth--;
                    running = false;
                    currentListener = prevListener;
                    err.then(() => { if (!disposed)
                        effect.execute(); });
                    return;
                }
                throw err;
            }
            finally {
                effectDepth--;
                running = false;
                currentListener = prevListener;
            }
            // If a signal write inside the effect triggered us (pendingRerun),
            // re-schedule the effect asynchronously. Use setTimeout instead of
            // queueMicrotask so the browser can paint between re-runs. This
            // prevents useEffect with no dependency array from creating an
            // infinite microtask loop that starves the rendering pipeline
            // (microtasks run before paint, setTimeout yields to paint first).
            // Limit self-reruns to match React's maximum update depth (50).
            if (pendingRerun) {
                pendingRerun = false;
                selfRerunCount++;
                if (selfRerunCount <= MAX_SELF_RERUNS) {
                    setTimeout(() => effect.execute(), 0);
                }
                else {
                    selfRerunCount = 0;
                }
            }
            else {
                // Reset self-rerun counter when the effect was triggered externally
                selfRerunCount = 0;
            }
        }
    };
    if (immediate) {
        effect.execute();
    }
    // Return dispose function.
    // Idempotent: an effect can now be reachable from two disposal channels at
    // once (a component's persisted `disposers` and an enclosing branch scope),
    // and running a user cleanup twice is a bug, not a no-op.
    const dispose = () => {
        if (disposed)
            return;
        disposed = true;
        if (cleanup && typeof cleanup === 'function') {
            cleanup();
            cleanup = null;
        }
        // Portalled nodes live outside every subtree the caller is removing, so
        // disposal is their only teardown (BUG-RUNTIME-088).
        _runPortalCleanups(effect);
        // Leave every subscriber set this effect joined (BUG-RUNTIME-187). A
        // disposed effect is inert but a Set entry is a strong reference, so
        // staying in costs the closure - and the DOM it closes over - forever.
        _untrackDeps(effect);
    };
    // Track disposer for component state persistence — when the component is
    // re-rendered, old effects are disposed to prevent zombie DOM manipulation.
    if (_componentEffectDisposers) {
        _componentEffectDisposers.push(dispose);
    }
    // Track disposer for the enclosing compiled conditional swap, whose next run
    // discards every node this render built (BUG-RUNTIME-088).
    if (_branchDisposers) {
        _branchDisposers.push(dispose);
    }
    // Track disposer for the enclosing component render (BUG-RUNTIME-088).
    // `renderCompiledComponent` parks this array on the node it returns as
    // `_effectDisposers`, and every teardown that discards that node already
    // drains it: the reactive-child codegen loop, `reactiveForPush`, the EB stub
    // swap, the keep-alive discard, and the compat unmount walk. Until now the
    // array only ever received deferEffect and throw-guard disposers, so a
    // component's OWN reactive effects survived the node they built - which is
    // how a portal opened in the body outlived the branch that rendered it.
    if (_effectCapture) {
        _effectCapture.push(dispose);
    }
    // Track disposer for the enclosing owner scope - a keyed row, which owns
    // everything created while its subtree was built (BUG-RUNTIME-187).
    if (_ownerDisposers) {
        _ownerDisposers.push(dispose);
    }
    return dispose;
}
// ---------------------------------------------------------------------------
// createEffectLite — lightweight effect for simple DOM bindings
// ---------------------------------------------------------------------------
/**
 * Host override for createEffectLite (the effect-primitive seam).
 *
 * A framework that embeds compiled output inside its own reactivity - JAF
 * hosting a compiled keyed list, for one - needs the per-binding effects to
 * subscribe to ITS graph as well as to this one, or a mutation only its graph
 * observes leaves the binding stale. Mirrors `setSignalCreationHook`: opt-in,
 * one null check per binding creation when unset, no cost on reads or writes.
 *
 * The wrapper receives the binding body and the built-in implementation, and
 * must return a disposer. It is expected to call the built-in exactly once, so
 * that dependency tracking here behaves exactly as it does without a host.
 */
export let _effectLiteImpl = null;
export function setEffectLiteImpl(fn) {
    _effectLiteImpl = fn;
}
export function createEffectLite(fn) {
    // The seam: unset (every JetShake-only build) this is one falsy check.
    if (_effectLiteImpl)
        return _effectLiteImpl(fn, createEffectLiteBase);
    return createEffectLiteBase(fn);
}
/**
 * Minimal reactive effect for simple DOM property/attribute bindings like:
 *   createEffectLite(() => { _t.data = __toDisplayString(count()) })
 *   createEffectLite(() => { _el.className = expr() })
 *
 * Tracks dependencies via currentListener (same as createEffect), runs fn
 * immediately, re-runs when dependencies change, returns a dispose function.
 *
 * Does NOT have: self-rerun detection, portal cleanup, promise/Suspense
 * handling, flushPendingValueAttrs, max depth protection, or
 * _componentEffectDisposers tracking.
 */
function createEffectLiteBase(fn) {
    let disposed = false;
    const effect = {
        execute() {
            if (disposed)
                return;
            const prevListener = currentListener;
            currentListener = effect;
            try {
                fn();
            }
            finally {
                currentListener = prevListener;
            }
        },
        // batch() flush protocol requires runCleanup/runSetup
        runCleanup() { },
        runSetup() {
            if (disposed)
                return;
            const prevListener = currentListener;
            currentListener = effect;
            try {
                fn();
            }
            finally {
                currentListener = prevListener;
            }
        }
    };
    effect.execute();
    // Idempotent, and it UNSUBSCRIBES (BUG-RUNTIME-187). A binding effect is the
    // only thing holding the element it writes to, so leaving it in the signal's
    // subscriber set retains the whole row after the row is gone.
    const dispose = () => {
        if (disposed)
            return;
        disposed = true;
        _untrackDeps(effect);
    };
    if (_ownerDisposers)
        _ownerDisposers.push(dispose);
    return dispose;
}
// ---------------------------------------------------------------------------
// createLayoutEffect — replaces useLayoutEffect
// ---------------------------------------------------------------------------
/**
 * Same as createEffect but runs synchronously after DOM mutations.
 * In our system, all effects are synchronous by default (no scheduler yet),
 * so this is currently identical to createEffect.
 */
export function createLayoutEffect(fn) {
    return createEffect(fn, true);
}
// ---------------------------------------------------------------------------
// deferEffect — defers effect creation to after DOM insertion
// ---------------------------------------------------------------------------
/**
 * compiledStableFn — lowering target for useCallback (BUG-COMPILER-029).
 *
 * The compiler used to lower `useCallback(fn, deps)` to bare `fn`, reasoning
 * that "the component runs once, so identity is stable". True for a PURE
 * compiled tree — and that stays the fast path: with no `_componentStateKey`
 * set this is one falsy check and the same `fn` the old codegen produced.
 *
 * It is NOT true when a compat parent re-renders a compiled child. The
 * fallback reconciler re-invokes the compiled body wholesale (BUG-COMPILER-009),
 * the function literal is re-evaluated, and identity churns on every parent
 * update — measured 3 churns over 3 updates in `.repro/src/n.jsx`. Any
 * downstream `memo()` comparator that inspects the callback prop then sees a
 * change every single time.
 *
 * Semantics are React's, not merely "stable":
 *   useCallback(fn, [])     → same function for the instance's lifetime
 *   useCallback(fn, [a, b]) → new function only when a dep changes
 *   useCallback(fn)         → new function every time (React's own rule)
 *
 * The no-deps case never reaches here: the compiler keeps emitting bare `fn`
 * for it. Pinning it would be a stale-closure bug — the opposite error.
 *
 * Lives in signal-core rather than its own module so it can read the slot
 * state directly; as a cross-module import the live bindings plus a setter
 * cost ~2x the bytes for the same behaviour.
 *
 * Slots are positional and share `_componentSlotIndex` with `createSignal`, so
 * the body must call these in a stable order across re-invocations — which the
 * compiler guarantees, since it only lowers top-level hook declarators.
 */
export function compiledStableFn(fn, deps) {
    // No key, or no entry, means no re-invocation can be in flight: return the
    // same `fn` the old codegen produced. setComponentStateKey always installs an
    // entry, so a missing one is not a case worth allocating for — bailing keeps
    // this helper small, which matters because it is pure overhead on the pure
    // compiled path.
    const entry = _componentStateKey && _componentStateMap && _componentStateMap.get(_componentStateKey);
    if (!entry)
        return fn;
    // The slot holds a { d, f } record, so the user's function is never mutated
    // with bookkeeping properties.
    const slot = entry.signals;
    const i = _componentSlotIndex++;
    const prev = slot[i];
    // Reuse only on an exact dep match. A length change counts as a change:
    // conditional hook order is already undefined behaviour, and re-deriving is
    // the safe side of that.
    if (prev && prev.d && prev.d.length === deps.length) {
        let same = true;
        for (let k = 0; k < deps.length; k++) {
            if (!Object.is(prev.d[k], deps[k])) {
                same = false;
                break;
            }
        }
        if (same)
            return prev.f;
    }
    slot[i] = { d: deps, f: fn };
    return fn;
}
// ---------------------------------------------------------------------------
// compiledMemo / compiledCallback — real dep semantics (BUG-COMPILER-054)
// ---------------------------------------------------------------------------
/** Object.is over two dep arrays; a length change counts as a change. */
function _sameDeps(a, b) {
    if (!a || !b || a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++)
        if (!Object.is(a[i], b[i]))
            return false;
    return true;
}
/**
 * One positional slot, or a throwaway record on the pure compiled path where
 * nothing re-invokes the body. Mirrors compiledStableFn: no state key means no
 * re-invocation can be in flight, so nothing needs persisting.
 */
function _hookRecord(brand) {
    const entry = _componentStateKey && _componentStateMap && _componentStateMap.get(_componentStateKey);
    if (!entry)
        return { b: brand };
    const slot = entry.signals;
    const i = _componentSlotIndex++;
    let rec = slot[i];
    if (!rec || rec.b !== brand) {
        rec = { b: brand };
        slot[i] = rec;
    }
    return rec;
}
/**
 * compiledMemo — lowering target for `useMemo(fn, deps)` when the deps array
 * does NOT cover every signal the factory reads (BUG-COMPILER-054).
 *
 * `createMemo(fn)` tracks whatever the factory touches, which is strictly
 * wider than React: an `[]` deps memo recomputes on every state change where
 * React pins it at mount. Here the tracked computation reads ONLY the deps
 * thunk, so the memo is dirtied exactly when a dep changes; the factory runs
 * untracked, so its own reads subscribe to nothing.
 *
 * The value is cached in a positional slot so a compat parent re-invoking the
 * body (BUG-COMPILER-009) does not re-run the factory either - the deps have
 * not changed, so React would not have re-run it.
 */
export function compiledMemo(factory, depsFn) {
    const rec = _hookRecord(1);
    const cell = createMemo(() => {
        const d = depsFn();
        if (!rec.h || !_sameDeps(rec.d, d)) {
            rec.d = d;
            rec.v = untrack(factory);
            rec.h = 1;
        }
        return rec.v;
    });
    // PRIME. createMemo is pull-based, so an unprimed cell runs its factory at
    // the first READ - which for a value nobody reads until later means it
    // captures state as of then, not as of the render React would have run it
    // in. React evaluates the factory where the hook sits; so do we.
    // `untrack` keeps this read from subscribing whatever listener is building
    // the component - the deps thunk still subscribes the memo's OWN
    // computation, because read() installs that before calling the body.
    untrack(cell);
    return cell;
}
/**
 * compiledCallback — lowering target for `useCallback(fn, deps)` when the
 * callback body reads a signal its deps do not list (BUG-COMPILER-054).
 *
 * `compiledStableFn` pins identity only; the body kept live `s0()` reads, so
 * `useCallback(() => setS0(s0 + 1), [])` went on applying fresh state where
 * React applies the mount-time closure forever. The compiler pins the reads
 * into locals inside `factory`, and this cell decides when that factory - and
 * therefore the closure it returns - is rebuilt.
 *
 * The returned wrapper's own identity is stable for the instance's lifetime,
 * which is what the pure compiled path already gave (a `const` evaluated once)
 * and what BUG-COMPILER-029 restored under a compat parent.
 */
export function compiledCallback(factory, depsFn) {
    const rec = _hookRecord(2);
    // Refreshed every invocation so a re-invoked body's closures are the ones
    // the cell calls, while the wrapper handed out keeps its identity.
    rec.f = factory;
    rec.g = depsFn;
    if (!rec.c) {
        rec.c = createMemo(() => {
            const d = rec.g();
            if (!rec.h || !_sameDeps(rec.d, d)) {
                rec.d = d;
                rec.v = untrack(rec.f);
                rec.h = 1;
            }
            return rec.v;
        });
        // PRIME - see compiledMemo. This one is load-bearing rather than tidy: a
        // callback is read at INVOCATION, so an unprimed cell would run the
        // factory on the first click and pin whatever state had reached by then.
        // Measured on fuzz `g0041-s2088155197`, where a plain handler moves the
        // state before the pinned callback is ever pressed - React writes
        // `a -> ab -> ab`, an unprimed cell writes `a -> ab -> abb`.
        untrack(rec.c);
        rec.w = function (...args) { return rec.c().apply(this, args); };
    }
    return rec.w;
}
/**
 * When _effectCapture is set (inside renderCompiledComponent), runs the
 * effect synchronously and captures the disposer for later cleanup.
 * Otherwise, uses queueMicrotask for normal async deferral.
 *
 * `layout` marks a `useLayoutEffect` / `useInsertionEffect` (BUG-COMPILER-058).
 * Both tiers keep sharing THIS registration channel - one callee, one import,
 * one positional slot cursor, so every multi-return and nested-block emission
 * site is untouched - and the flag decides only WHEN the effect runs.
 *
 * Why it has to: a layout effect's one observable promise is that it has
 * ALREADY RUN by the time the render that declared it returns to its caller.
 * React commits layout effects synchronously (docs/EFFECT-ORDERING.md step 8,
 * before the microtask boundary at step 11), so this holds:
 *
 *     render(<C/>)               // the useLayoutEffect inside C has run
 *     fireEvent.click(button)    // the handler sees its writes
 *
 * A microtask-deferred effect does not run there, and nothing rescues it -
 * `act()` drains the effect QUEUES, not the microtask queue. So a test that
 * clicks on the line after `render()` saw the pre-effect world. That is
 * BUG-COMPILER-058: valtio's `useProxy` flips a `let isRendering` in a bare
 * layout effect and its Proxy trap serves frozen SNAPSHOT values until it
 * flips, so the click wrote to a frozen object and threw.
 *
 * WHERE THE LAYOUT TIER RUNS. When a compat component is on the stack a commit
 * is coming, and that commit drains the shared layout-effect queue at exactly
 * the right phase (DOM mutated, refs attached, before passive effects) - so
 * hand it over and let the commit coordinator own the timing. That covers both
 * hosted shapes: a compiled component under a compat parent, and a compiled
 * custom hook called by a compat consumer, which is how compile-through
 * delivers a vendor package.
 *
 * With NO compat frame there is no commit to join, so the microtask deferral
 * stays exactly as it was and the pure compiled path is unchanged. The queue is
 * reached through `globalThis` rather than by importing `shared/effects.js`
 * because this module's DCE audit (see the file header) requires it to keep its
 * single import.
 */
export function deferEffect(fn, layout) {
    // Component state persistence: if we're inside a re-render with persisted state,
    // skip deferred effects that already ran (mount-only effects like useEffect(fn, [])).
    if (_componentStateKey && _componentStateMap) {
        const entry = _componentStateMap.get(_componentStateKey);
        if (entry && _componentDeferIndex < entry.deferCount) {
            _componentDeferIndex++;
            return;
        }
        _componentDeferIndex++;
        if (entry) {
            entry.deferCount = _componentDeferIndex;
        }
    }
    if (layout) {
        const seam = globalThis.__REACT_COMPILER_SEAM__;
        const host = seam && seam.compatHookHost;
        const effects = globalThis.__REACT_COMPILER_EFFECTS__;
        const queue = effects && effects.pendingLayoutEffectCallbacks;
        if (queue && host && host.currentComponent && host.currentComponent() != null) {
            const capture = _effectCapture;
            // The queue's element type is compat's SCHEDULED SYNC EFFECT record, not
            // a bare callback: `flushScheduledSyncEffects` reads `hook`, `versionKey`
            // and `version` to decide whether the effect is still current, and writes
            // the returned cleanup back onto `hook`. A compiled effect has no compat
            // hook slot to be invalidated against, so it carries a private
            // one-property stand-in whose version always matches - "always still
            // current" - and the cleanup it returns is the createEffect disposer,
            // which is owned by `_effectCapture` and is therefore parked on the
            // stand-in rather than run from here.
            queue.push({
                hook: { v: 1 },
                versionKey: 'v',
                version: 1,
                nextDeps: null,
                create: () => {
                    const result = fn();
                    if (capture !== null && typeof result === 'function')
                        capture.push(result);
                    return null;
                },
            });
            return;
        }
    }
    if (_effectCapture !== null) {
        // Capture the disposer array reference so we can add to it asynchronously.
        // Effects must still defer via queueMicrotask so that DOM elements are
        // in the document when the effect runs (e.g. document.getElementById).
        const capture = _effectCapture;
        queueMicrotask(() => {
            const result = fn();
            if (typeof result === 'function')
                capture.push(result);
        });
    }
    else {
        queueMicrotask(fn);
    }
}
/**
 * BUG-RUNTIME-123: like deferEffect, but re-installs on every body execution.
 *
 * Used when a lowered useEffect has deps that are NOT signals (e.g. `api` from
 * `useEmblaCarousel`). A plain createEffect closes over the first render's
 * values and never sees later ones; deferEffect's deferCount skip then leaves
 * that stale install in place. replaceEffect disposes the prior install at
 * this slot and creates a fresh effect whose closure sees the current values —
 * React's "[dep] changed → re-run" semantics without retaining React useEffect.
 */
export function replaceEffect(fn) {
    const key = _componentStateKey;
    const entry = key && _componentStateMap ? _componentStateMap.get(key) : null;
    const idx = _componentDeferIndex++;
    if (entry) {
        if (!entry.replaceSlots)
            entry.replaceSlots = [];
        const prev = entry.replaceSlots[idx];
        if (typeof prev === 'function') {
            try {
                prev();
            }
            catch (_e) { /* disposer must not break the re-install */ }
        }
        entry.replaceSlots[idx] = null;
        entry.deferCount = Math.max(entry.deferCount || 0, _componentDeferIndex);
    }
    const install = () => {
        const result = fn();
        if (entry) {
            if (!entry.replaceSlots)
                entry.replaceSlots = [];
            entry.replaceSlots[idx] = typeof result === 'function' ? result : null;
        }
        return result;
    };
    if (_effectCapture !== null) {
        const capture = _effectCapture;
        queueMicrotask(() => {
            const result = install();
            if (typeof result === 'function')
                capture.push(result);
        });
    }
    else {
        queueMicrotask(install);
    }
}
// ---------------------------------------------------------------------------
// batch — groups multiple signal updates into a single flush
// ---------------------------------------------------------------------------
/**
 * Replaces React's automatic batching. Prevents intermediate renders.
 */
export function batch(fn) {
    batchDepth++;
    let result;
    try {
        result = fn();
    }
    finally {
        batchDepth--;
        if (batchDepth === 0) {
            // Flush all pending effects. Effects may queue more effects during
            // execution, so we loop until the queue is empty (with depth protection).
            let flushIterations = 0;
            while (pendingEffects.length > 0) {
                if (flushIterations++ >= MAX_EFFECT_DEPTH) {
                    if (typeof console !== 'undefined') {
                        console.error('Maximum batch flush iterations exceeded (' + MAX_EFFECT_DEPTH + '). ' +
                            'Dropping ' + pendingEffects.length + ' pending effects.');
                    }
                    pendingEffects = [];
                    break;
                }
                const effects = pendingEffects.slice();
                pendingEffects = [];
                // Two-pass flush: run all cleanups first, then all setups.
                // This matches React's effect ordering contract where all
                // cleanup functions fire before any new setup functions.
                for (const effect of effects) {
                    effect.runCleanup();
                }
                batchDepth++;
                for (const effect of effects) {
                    effect.runSetup();
                }
                batchDepth--;
            }
            // Flush value attribute updates queued by effects during this batch.
            // Skips the focused element to match React's behavior (no attribute
            // update while the user is actively typing in an input).
            flushPendingValueAttrs();
        }
    }
    return result;
}
// ---------------------------------------------------------------------------
// suspendBatch / restoreBatch
// ---------------------------------------------------------------------------
/**
 * Suspend and restore batch depth.
 * Used by flushSync to temporarily break out of an active batch,
 * ensuring signal updates flush immediately.
 */
export function suspendBatch() {
    const saved = batchDepth;
    batchDepth = 0;
    return saved;
}
export function restoreBatch(saved) {
    batchDepth = saved;
}
// ---------------------------------------------------------------------------
// untrack — run a function without tracking signal reads
// ---------------------------------------------------------------------------
export function untrack(fn) {
    const prevListener = currentListener;
    currentListener = null;
    try {
        return fn();
    }
    finally {
        currentListener = prevListener;
    }
}
// ---- setComponentStateKey ----
export function setComponentStateKey(key, reactKey, preserveDeferCount) {
    if (key) {
        // BUG-RUNTIME-085/089: per-pass epoch for the fallback reconciler's
        // occurrence counter, which is how it tells two call sites of the SAME
        // compat component apart under ONE state key. `ComponentInstance.render`
        // bumps its own `__fbEpoch` right before installing itself as the key
        // (hooks.ts); a compiled body's key is a DOM marker installed HERE, so this
        // is the equivalent point. Without it the counter never resets and the
        // second pass keys every call site one slot further along.
        // BUG-RUNTIME-120: this used to be gated on `key instanceof Node`, on the
        // reading that only a DOM marker names a whole body. `keyedList`'s per-row
        // key is a plain object installed through THIS function and it names a whole
        // ROW body - the identical situation - so it needs the identical reset. It
        // did not get one only because its key used to be re-minted on every pass,
        // which reset the counter by accident; once the key is genuinely stable
        // (the 120 fix) the ordinal climbs 0,1,2,3… and every hosted compat child
        // misses its cache on the SECOND pass instead of the first. Bumping for any
        // key installed here is the general rule.
        //
        // This cannot reach an ANCHOR key, and must not: BUG-RUNTIME-099(a)'s
        // ordinal is positional across a whole compiled SUBTREE and resetting it
        // re-pairs live compat children with other call sites. Anchor slots are
        // installed DIRECTLY, never through `setComponentStateKey` (BUG-RUNTIME-086
        // rule 2), and additionally take the `site` branch in
        // `fallbackReconcileComponent` rather than the ordinal one.
        key.__fbEpoch = (key.__fbEpoch || 0) + 1;
        const prev = _componentStateMap && _componentStateMap.get(key);
        if (prev && prev.disposers) {
            for (const dispose of prev.disposers)
                dispose();
            prev.disposers = [];
            if (!preserveDeferCount) {
                prev.deferCount = 0;
            }
            // BUG-RUNTIME-123: replaceEffect slots outlive entry.disposers on the
            // ComponentInstance path (deferred installs never make it into
            // _componentEffectDisposers). Dispose them on every body re-key so a
            // leaked prior createEffect cannot keep a stale closure alive next to
            // the fresh install.
            if (prev.replaceSlots) {
                for (const dispose of prev.replaceSlots) {
                    if (typeof dispose === 'function') {
                        try {
                            dispose();
                        }
                        catch (_e) { }
                    }
                }
                prev.replaceSlots = [];
            }
            if (prev._ebIndex !== undefined)
                prev._ebIndex = 0;
            // Per-render slot cursors for reactiveComponentAnchor / reactiveHostAnchor
            // (BUG-RUNTIME-061), reset with the same discipline as _componentSlotIndex.
            if (prev._anchorIndex !== undefined)
                prev._anchorIndex = null;
            // BUG-RUNTIME-068: memo-gate occurrence counter is positional within one
            // body invocation, so it resets here alongside the other per-pass indices.
            if (prev._memoOcc)
                prev._memoOcc = new Map();
            if (prev._ebInstances && prev._ebInstances.size > 0) {
                prev._ebGeneration = (prev._ebGeneration || 0) + 1;
            }
        }
        if (prev && reactKey !== undefined && prev.reactKey !== reactKey) {
            prev.signals = [];
            prev.deferCount = 0;
            prev.reactKey = reactKey;
            // A react-key change is a REMOUNT: the old memo gates describe DOM that
            // belongs to the previous identity and must not be reused (BUG-RUNTIME-068).
            if (prev._memoGates)
                prev._memoGates = new Map();
            if (prev._ebInstances)
                prev._ebInstances = new Map();
            // BUG-RUNTIME-114 host persistence keys off marker._jsLiveNodes. A
            // react-key change is a REMOUNT: the live tree belongs to the previous
            // identity and must be swapped, not patched in place — otherwise the
            // fresh body's useEffect/state bindings land on a discarded tree while
            // the live DOM keeps stale bindings (GrowKeySwapRemount /
            // AdvancedKeyReset regressions after c86a3b22).
            if (key && typeof key === 'object' && key.nodeType === 8) {
                key._jsLiveNodes = null;
                key._jsRenderClone = null;
            }
        }
        else if (!prev) {
            _componentStateMap.set(key, { signals: [], disposers: [], deferCount: 0, reactKey });
        }
        _componentEffectDisposers = [];
    }
    else {
        if (_componentStateKey && _componentStateMap && _componentEffectDisposers) {
            let entry = _componentStateMap.get(_componentStateKey);
            if (entry) {
                entry.disposers = _componentEffectDisposers;
                if (entry._ebInstances && entry._ebGeneration) {
                    const gen = entry._ebGeneration;
                    for (const [Comp, instances] of entry._ebInstances) {
                        const kept = instances.filter(inst => inst && inst._ebVisitedGen === gen);
                        if (kept.length > 0) {
                            entry._ebInstances.set(Comp, kept);
                        }
                        else {
                            entry._ebInstances.delete(Comp);
                        }
                    }
                }
            }
        }
        _componentEffectDisposers = null;
    }
    _componentStateKey = key;
    _componentSlotIndex = 0;
    _componentDeferIndex = 0;
}
